import type { KryspaceRenderManifest, RenderManifestQueueItem } from '../../types/kryspaceRenderManifest';
import type { ScopableItem } from './filters';
import { fixUtf8Mojibake } from './fixUtf8Mojibake';
import { labelGuidancePhrase } from './controlBridgeModelOptions';
import { parseAttentionRoutingFromManifest, deriveAttentionRoutingFallback, type AttentionRouting } from './attentionRouting';
import {
  deriveMcDomainVgrFallback,
  parseMcDomainVgrFromManifest,
  type McDomainKey,
  type McDomainVgrModel,
} from './missionControl/mcDomainVgr';
import {
  deriveMcNextMoveFallback,
  parseMcNextMoveFromManifest,
  type McNextMoveModel,
} from './missionControl/mcNextMove';
import {
  parseMcEngineeringWorkspacesFromManifest,
  type McEngineeringWorkspacesModel,
} from './missionControl/mcEngineeringWorkspaces';
import {
  parseBridgeSessionAssessmentFromManifest,
  type BridgeSessionAssessmentModel,
} from './missionControl/bridgeSessionAssessment';
import { parseGuidanceBacklogFromManifest } from './guidance/guidanceBacklogManifest';
import type { GuidanceBacklogEntry } from './guidance/guidanceBacklogHelpers';
import {
  parseExoPanelCalendarFromManifest,
  type ExoPanelCalendarModel,
} from './exoPanel/exoPanelCalendarManifest';
import { deriveActorMarker, type ActorMarker } from './derivation/actorMarker';
import { parseRoleLensFromManifest, type RoleLens } from './derivation/roleLens';
import { normalizeBlockerClass, type BlockerClass } from './derivation/clickTarget';
import {
  parseFlowEnvelopeFromManifest,
  type FlowModel,
} from './derivation/flowStage';

function mcLaunchSuppressions(manifest: KryspaceRenderManifest):
  | { pipeline?: string; domains?: string[] }
  | undefined {
  const section =
    manifest.sections.find((s) => s.id === 'mc-domain-vgr') ??
    manifest.sections.find((s) => s.id === 'mc-next-move');
  const raw = section?.meta?.launchSuppressions;
  if (!raw || typeof raw !== 'object') return undefined;
  return raw;
}

export type ControlBridgeSurfaceKey =
  | 'mission'
  | 'drive'
  | 'guidance'
  | 'diagnose'
  | 'issues'
  | 'exopanel'
  | 'processflow'
  | 'dailybrief'
  | 'organizer'
  | 'client';

/** Surfaces selectable as tabs (Mission Control lives above tabs, not as a tab). */
export type ControlBridgeWorkSurfaceKey = Exclude<ControlBridgeSurfaceKey, 'mission'>;

export interface ControlBridgeRollup {
  bridgeSession: string | number | boolean | null;
  waypoints: string | number | boolean | null;
  waypointsTotal: string | number | boolean | null;
  openImprovements: string | number | boolean | null;
  activeActivities: string | number | boolean | null;
  openIssues: string | number | boolean | null;
}

export interface ControlBridgeQueueItem extends RenderManifestQueueItem, ScopableItem {
  filterScope?: string;
  itemIdAliases?: string[];
  postgresItemId?: string;
  semanticItemId?: string;
  kryspaceItemId?: string;
  assignedResourceRef?: string;
  assignedResourceLabel?: string;
  waypointRef?: string;
  improvementRef?: string;
  improvementTitle?: string;
  activityTargetState?: string;
  issueType?: string;
  issueSummary?: string;
  activityDescription?: string;
  coordinationNoteRef?: string;
  blockedReason?: string;
  deferReason?: string;
  dependsOnRefs?: string[];
  acceptanceHints?: string[];
  workHref?: string;
  plankDescription?: string;
  futureCondition?: string;
  resourceAssignment?: string;
  assumptionText?: string;
  estimatedDuration?: string;
  startDate?: string;
  endDate?: string;
  duration?: string;
  pickupKind?: string;
  blockerClass?: BlockerClass;
  resourceAvailable?: boolean;
  requiresBridgeSession?: boolean;
  actorMarker?: ActorMarker | null;
  /** ExoPanel U1 — prefer responseSchema (A6); userResponseType is render hint. */
  userResponseType?: string;
  userResponsePrompt?: string;
  userResponseOptions?: Array<{ label?: string; value?: string } | string>;
  userResponseTargetPropertyKeys?: string[];
  responseSchema?: Array<Record<string, unknown>>;
  responseSchemaTargetRef?: string;
  responseSchemaTargetState?: string;
  responseSchemaBlockedReason?: string;
  /** BE-PKG transition contract — tracked artifact next hop (not activity SM). */
  transitionContract?: {
    trackedArtifactRef?: string;
    trackedCurrentState?: string;
    trackedArtifactTypeRef?: string;
    vocabularyPlane?: string;
    transition?: { fromState?: string; toState?: string };
    changedProperties?: Array<{
      propertyKey?: string;
      stateActionType?: string;
      actorKind?: string;
      shape?: string;
    }>;
    status?: string;
    blockedReason?: string;
  };
  transitionContractStatus?: string;
  transitionContractBlockedReason?: string;
  /** Opgen A4 — tracked-record chrome (prefer over activityDescription). */
  recordContext?: {
    trackedArtifactRef?: string;
    name?: string;
    customer?: string;
    value?: string;
    ageAtStep?: string;
    lastTouch?: string;
    owner?: string;
  };
}

export type ControlBridgeWaypointItem = ControlBridgeQueueItem;
export type ControlBridgeTimelineItem = ControlBridgeQueueItem;
export type ControlBridgeLinkedWorkspace = ControlBridgeQueueItem;

export interface ControlBridgeViewModel {
  companyTitle: string;
  workspaceSlug: string;
  generatedAt: string;
  rollup: ControlBridgeRollup;
  recommendedFocus: ControlBridgeQueueItem[];
  eligibleWork: ControlBridgeQueueItem[];
  openIssues: ControlBridgeQueueItem[];
  /** Orient blocked_activities (pickup gates); not in Eligible work. */
  blockedWork: ControlBridgeQueueItem[];
  blockedActivityCount: number;
  waypoints: ControlBridgeWaypointItem[];
  guidanceTimeline: ControlBridgeTimelineItem[];
  linkedWorkspaces: ControlBridgeLinkedWorkspace[];
  scopeHealthQueue: ControlBridgeQueueItem[];
  guidanceWbsQueue: ControlBridgeQueueItem[];
  overallEntropy: number | null;
  healthSource?: string;
  guidanceSource?: 'manifest' | 'inferred';
  /** Guidance/Drive hi-fi personal layer from manifest section when present. */
  personalLayerSource?: 'manifest' | 'proxy';
  /** Mission Control attention routing (manifest-first). */
  attentionRouting?: AttentionRouting;
  /** Mission Control domain VGR trio (manifest-first). */
  mcDomainVgr?: McDomainVgrModel;
  mcDomainVgrSource?: 'manifest' | 'fallback';
  mostCriticalDomain?: McDomainKey | null;
  /** Mission Control next-move block (manifest-first). */
  mcNextMove?: McNextMoveModel;
  mcNextMoveSource?: 'manifest' | 'fallback';
  /** Guidance backlog proposals (manifest-first). */
  guidanceBacklogProposals?: GuidanceBacklogEntry[];
  guidanceBacklogSource?: 'manifest' | 'empty';
  /** ExoPanel calendar strip (manifest-first). */
  exoPanelCalendar?: ExoPanelCalendarModel;
  exoPanelCalendarSource?: 'manifest' | 'empty';
  /** Parent product engineering workspace links (krysalis_central). */
  mcEngineeringWorkspaces?: McEngineeringWorkspacesModel;
  /** Workshop entropy heat map + friction priorities from bridge session writeback. */
  bridgeSessionAssessment?: BridgeSessionAssessmentModel;
  bridgeSessionAssessmentSource?: 'manifest' | 'empty';
  /** Role domain selection (manifest-first; provisional applied in shell when absent). */
  roleLens?: RoleLens | null;
  roleLensSource?: 'manifest' | 'empty';
  /** Drive envelope (manifest). Process Flow tab is retired; envelope stays on Drive. */
  flowModel?: FlowModel | null;
  flowModelSource?: 'manifest' | 'envelope' | 'empty';
}

export const CONTROL_BRIDGE_SURFACES: Array<{
  key: ControlBridgeSurfaceKey;
  label: string;
  blurb: string;
}> = [
  { key: 'mission', label: 'Mission Control', blurb: 'What matters most, right now?' },
  { key: 'drive', label: 'Drive', blurb: "What's flowing, and where is it blocked?" },
  { key: 'processflow', label: 'Process Flow', blurb: 'How does the hierarchy roll up?' },
  { key: 'diagnose', label: 'Diagnostics', blurb: 'How is the system operating?' },
  { key: 'issues', label: 'Issues', blurb: 'Filed issues for this workspace.' },
  { key: 'guidance', label: 'Guidance', blurb: 'Where are we headed?' },
  { key: 'exopanel', label: 'Exo Panel', blurb: 'What do I need to do?' },
  { key: 'dailybrief', label: 'Daily Brief', blurb: "Today's picture for this lens." },
  { key: 'organizer', label: 'Personal Organizer', blurb: 'My load and offload.' },
  { key: 'client', label: 'Client Status', blurb: 'Where is my delivery?' },
];

/**
 * FE-SURF-1 (2026-09-30, `co-podr-dec-drive-cb-product-picks-20260930`):
 * Process Flow is retired (hierarchy is Drive zoom). Daily Brief and Client Status
 * stay hidden until a dated change-feed and one-account Delivery exist.
 * Issues is not a peer tab (`co-kfo-pia-issues-merge-into-diagnostics-20261007`);
 * issue stats and the list live on Diagnostics.
 */
export type ControlBridgeHeldSurfaceKey = 'processflow' | 'dailybrief' | 'client';

export function isHeldControlBridgeSurface(key: string): key is ControlBridgeHeldSurfaceKey {
  return key === 'processflow' || key === 'dailybrief' || key === 'client';
}

export function controlBridgeTabIsOffered(key: ControlBridgeSurfaceKey): boolean {
  if (key === 'mission' || key === 'issues' || isHeldControlBridgeSurface(key)) {
    return false;
  }
  return true;
}

export const CONTROL_BRIDGE_HELD_SURFACE_COPY: Record<
  ControlBridgeHeldSurfaceKey,
  { title: string; body: string }
> = {
  processflow: {
    title: 'Process Flow is not a Control Bridge tab',
    body: 'Hierarchy drill belongs on Drive when that zoom exists. Deep links to this key do not open a flow board.',
  },
  dailybrief: {
    title: 'Daily Brief is not available yet',
    body: 'This surface stays hidden until a dated change-feed exists. Deep links do not open an eligible-work list.',
  },
  client: {
    title: 'Client Status is not available yet',
    body: 'This surface stays hidden until one-account Delivery exists. Deep links do not open a filtered work list.',
  },
};

export const CONTROL_BRIDGE_TAB_SURFACES = CONTROL_BRIDGE_SURFACES.filter((s) =>
  controlBridgeTabIsOffered(s.key)
) as Array<{ key: ControlBridgeWorkSurfaceKey; label: string; blurb: string }>;

function rollupField(
  manifest: KryspaceRenderManifest,
  key: keyof ControlBridgeRollup
): string | number | boolean | null {
  const rollup = manifest.sections.find((s) => s.id === 'rollup');
  const fieldKey =
    key === 'bridgeSession'
      ? 'bridge_session'
      : key === 'openImprovements'
        ? 'open_improvements'
        : key === 'activeActivities'
          ? 'active_activities'
          : key === 'openIssues'
            ? 'open_issues'
            : key === 'waypointsTotal'
              ? 'waypoints_total'
              : key;
  const field = rollup?.fields?.find((f) => f.key === fieldKey);
  return field?.value ?? null;
}

function queueRowField(row: RenderManifestQueueItem, key: string): unknown {
  const raw = row as RenderManifestQueueItem & Record<string, unknown>;
  return raw[key];
}

function sanitizeText(value: string | undefined): string | undefined {
  if (value == null || value === '') return value;
  return fixUtf8Mojibake(value);
}

function sanitizeRollupValue(value: string | number | boolean | null): string | number | boolean | null {
  if (typeof value === 'string') return fixUtf8Mojibake(value);
  return value;
}

function firstMappedString(row: RenderManifestQueueItem, keys: string[]): string | undefined {
  for (const key of keys) {
    const camel = key.includes('_')
      ? key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      : key;
    const fromCamel = queueRowField(row, camel);
    if (typeof fromCamel === 'string' && fromCamel.trim()) {
      return fromCamel.trim();
    }
    const fromRaw = queueRowField(row, key);
    if (typeof fromRaw === 'string' && fromRaw.trim()) {
      return fromRaw.trim();
    }
  }
  return undefined;
}

function mappedStringList(row: RenderManifestQueueItem, keys: string[]): string[] | undefined {
  for (const key of keys) {
    const camel = key.includes('_')
      ? key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      : key;
    for (const candidate of [queueRowField(row, camel), queueRowField(row, key)]) {
      if (!Array.isArray(candidate)) continue;
      const out = candidate
        .filter((x): x is string => typeof x === 'string' && Boolean(x.trim()))
        .map((x) => x.trim());
      if (out.length) return out;
    }
  }
  return undefined;
}

function mapQueueItem(row: RenderManifestQueueItem): ControlBridgeQueueItem {
  const itemId =
    (typeof row.itemId === 'string' && row.itemId) ||
    (typeof queueRowField(row, 'item_id') === 'string' && (queueRowField(row, 'item_id') as string)) ||
    '';
  const waypointRef =
    (typeof row.waypointRef === 'string' && row.waypointRef) ||
    (typeof queueRowField(row, 'waypoint_ref') === 'string'
      ? (queueRowField(row, 'waypoint_ref') as string)
      : undefined);
  const improvementRef =
    (typeof row.improvementRef === 'string' && row.improvementRef) ||
    (typeof queueRowField(row, 'improvement_ref') === 'string'
      ? (queueRowField(row, 'improvement_ref') as string)
      : undefined) ||
    (typeof queueRowField(row, 'improvement_activity_ref') === 'string'
      ? (queueRowField(row, 'improvement_activity_ref') as string)
      : undefined);
  const activityDescription = sanitizeText(
    firstMappedString(row, ['activityDescription', 'activity_description', 'description'])
  );
  const coordinationNoteRef = firstMappedString(row, [
    'coordinationNoteRef',
    'coordination_note_ref',
  ]);
  const blockedReason = sanitizeText(
    firstMappedString(row, ['blockedReason', 'blocked_reason'])
  );
  const deferReason = sanitizeText(
    firstMappedString(row, ['deferReason', 'defer_reason'])
  );
  const dependsOnRefs = mappedStringList(row, ['dependsOnRefs', 'depends_on_refs']);
  const acceptanceHints = mappedStringList(row, ['acceptanceHints', 'acceptance_hints']);
  const activityTargetState = firstMappedString(row, [
    'activityTargetState',
    'activity_target_state',
    'target_state',
  ]);
  const workHref = firstMappedString(row, ['workHref', 'work_href', 'triage_href']);
  const triageRoute = firstMappedString(row, ['triageRoute', 'triage_route']);
  const plankDescription = sanitizeText(
    firstMappedString(row, ['plankDescription', 'plank_description'])
  );
  const futureCondition = sanitizeText(
    firstMappedString(row, ['futureCondition', 'future_condition'])
  );
  const resourceAssignment = sanitizeText(
    firstMappedString(row, ['resourceAssignment', 'resource_assignment'])
  );
  const assumptionText = sanitizeText(
    firstMappedString(row, ['assumptionText', 'assumption_text'])
  );
  const estimatedDuration = sanitizeText(
    firstMappedString(row, ['estimatedDuration', 'estimated_duration'])
  );
  const startDate = firstMappedString(row, ['startDate', 'start_date']);
  const endDate = firstMappedString(row, ['endDate', 'end_date']);
  const duration = sanitizeText(firstMappedString(row, ['duration']));
  const pickupKind = firstMappedString(row, ['pickupKind', 'pickup_kind']);
  const blockerClass = normalizeBlockerClass(
    firstMappedString(row, ['blockerClass', 'blocker_class', 'blockerClass'])
  );
  const resourceAvailableRaw = queueRowField(row, 'resourceAvailable') ?? queueRowField(row, 'resource_available');
  const requiresBridgeSessionRaw =
    queueRowField(row, 'requiresBridgeSession') ?? queueRowField(row, 'requires_bridge_session');
  const resourceAvailable =
    typeof resourceAvailableRaw === 'boolean'
      ? resourceAvailableRaw
      : resourceAvailableRaw === undefined || resourceAvailableRaw === null
        ? undefined
        : String(resourceAvailableRaw).toLowerCase() === 'true';
  const requiresBridgeSession =
    typeof requiresBridgeSessionRaw === 'boolean'
      ? requiresBridgeSessionRaw
      : requiresBridgeSessionRaw === undefined || requiresBridgeSessionRaw === null
        ? undefined
        : String(requiresBridgeSessionRaw).toLowerCase() === 'true';
  const assignedResourceRef =
    (typeof row.assignedResourceRef === 'string' && row.assignedResourceRef) ||
    (typeof queueRowField(row, 'assigned_resource_ref') === 'string'
      ? (queueRowField(row, 'assigned_resource_ref') as string)
      : undefined);
  const assignedResourceLabel = sanitizeText(
    (typeof row.assignedResourceLabel === 'string' && row.assignedResourceLabel) ||
      (typeof queueRowField(row, 'assigned_resource_label') === 'string'
        ? (queueRowField(row, 'assigned_resource_label') as string)
        : undefined) ||
      (typeof queueRowField(row, 'assigned_resource_name') === 'string'
        ? (queueRowField(row, 'assigned_resource_name') as string)
        : undefined)
  );
  const resToken = row.res;
  // Prefer explicit resource_filter; else derive chip/filter token from assignee ref
  // so Personal Organizer / resource bar stay linked when assignedResourceRef is set alone.
  const resFromAssignee =
    typeof assignedResourceRef === 'string' && assignedResourceRef.trim()
      ? assignedResourceRef.trim().toLowerCase().startsWith('person:')
        ? assignedResourceRef.trim().slice(7)
        : assignedResourceRef.trim()
      : undefined;
  const res =
    (typeof resToken === 'string' && resToken.trim() ? resToken.trim() : undefined) ||
    resFromAssignee;
  const actorMarker = deriveActorMarker({
    pickupKind,
    assignedResourceRef,
    assignedResourceLabel,
    res,
  });
  const postgresItemId = firstMappedString(row, ['postgresItemId', 'postgres_item_id']);
  const semanticItemId = firstMappedString(row, ['semanticItemId', 'semantic_item_id']);
  const kryspaceItemId = firstMappedString(row, ['kryspaceItemId', 'kryspace_item_id']);
  const itemIdAliases = mappedStringList(row, ['itemIdAliases', 'item_id_aliases']);
  const userResponseType =
    firstMappedString(row, ['userResponseType', 'user_response_type']) || 'binary_disposition';
  const userResponsePrompt = sanitizeText(
    firstMappedString(row, ['userResponsePrompt', 'user_response_prompt'])
  );
  const userResponseTargetPropertyKeys = mappedStringList(row, [
    'userResponseTargetPropertyKeys',
    'user_response_target_property_keys',
  ]);
  let userResponseOptions: Array<{ label?: string; value?: string } | string> | undefined;
  const optsRaw =
    queueRowField(row, 'userResponseOptions') ?? queueRowField(row, 'user_response_options');
  if (Array.isArray(optsRaw) && optsRaw.length) {
    userResponseOptions = optsRaw as Array<{ label?: string; value?: string } | string>;
  }
  const responseSchemaRaw =
    queueRowField(row, 'responseSchema') ?? queueRowField(row, 'response_schema');
  const responseSchema = Array.isArray(responseSchemaRaw)
    ? (responseSchemaRaw as Array<Record<string, unknown>>)
    : undefined;
  const responseSchemaTargetRef = firstMappedString(row, [
    'responseSchemaTargetRef',
    'response_schema_target_ref',
  ]);
  const responseSchemaTargetState = firstMappedString(row, [
    'responseSchemaTargetState',
    'response_schema_target_state',
  ]);
  const responseSchemaBlockedReason = firstMappedString(row, [
    'responseSchemaBlockedReason',
    'response_schema_blocked_reason',
  ]);
  let transitionContract: ControlBridgeQueueItem['transitionContract'];
  const contractRaw =
    queueRowField(row, 'transitionContract') ?? queueRowField(row, 'transition_contract');
  if (contractRaw && typeof contractRaw === 'object' && !Array.isArray(contractRaw)) {
    const tc = contractRaw as Record<string, unknown>;
    const pick = (...keys: string[]): string | undefined => {
      for (const k of keys) {
        const v = tc[k];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return undefined;
    };
    let transition: { fromState?: string; toState?: string } | undefined;
    const trRaw = tc.transition;
    if (trRaw && typeof trRaw === 'object' && !Array.isArray(trRaw)) {
      const tr = trRaw as Record<string, unknown>;
      const fromState =
        typeof tr.fromState === 'string'
          ? tr.fromState.trim()
          : typeof tr.from_state === 'string'
            ? tr.from_state.trim()
            : undefined;
      const toState =
        typeof tr.toState === 'string'
          ? tr.toState.trim()
          : typeof tr.to_state === 'string'
            ? tr.to_state.trim()
            : undefined;
      if (fromState || toState) {
        transition = { fromState: fromState || undefined, toState: toState || undefined };
      }
    }
    let changedProperties: NonNullable<
      ControlBridgeQueueItem['transitionContract']
    >['changedProperties'];
    const chRaw = tc.changedProperties ?? tc.changed_properties;
    if (Array.isArray(chRaw) && chRaw.length) {
      changedProperties = chRaw
        .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
        .map((e) => ({
          propertyKey:
            typeof e.propertyKey === 'string'
              ? e.propertyKey
              : typeof e.property_key === 'string'
                ? e.property_key
                : undefined,
          stateActionType:
            typeof e.stateActionType === 'string'
              ? e.stateActionType
              : typeof e.state_action_type === 'string'
                ? e.state_action_type
                : undefined,
          actorKind:
            typeof e.actorKind === 'string'
              ? e.actorKind
              : typeof e.actor_kind === 'string'
                ? e.actor_kind
                : undefined,
          shape: typeof e.shape === 'string' ? e.shape : undefined,
        }));
    }
    transitionContract = {
      trackedArtifactRef: pick('trackedArtifactRef', 'tracked_artifact_ref'),
      trackedCurrentState: pick('trackedCurrentState', 'tracked_current_state'),
      trackedArtifactTypeRef: pick('trackedArtifactTypeRef', 'tracked_artifact_type_ref'),
      vocabularyPlane: pick('vocabularyPlane', 'vocabulary_plane'),
      transition,
      changedProperties,
      status: pick('status'),
      blockedReason: pick('blockedReason', 'blocked_reason'),
    };
    if (
      !transitionContract.trackedArtifactRef &&
      !transitionContract.transition &&
      !transitionContract.status &&
      !(transitionContract.changedProperties && transitionContract.changedProperties.length)
    ) {
      transitionContract = undefined;
    }
  }
  const transitionContractStatus = firstMappedString(row, [
    'transitionContractStatus',
    'transition_contract_status',
  ]);
  const transitionContractBlockedReason = firstMappedString(row, [
    'transitionContractBlockedReason',
    'transition_contract_blocked_reason',
  ]);
  let recordContext: ControlBridgeQueueItem['recordContext'];
  const recordRaw =
    queueRowField(row, 'recordContext') ?? queueRowField(row, 'record_context');
  if (recordRaw && typeof recordRaw === 'object' && !Array.isArray(recordRaw)) {
    const rc = recordRaw as Record<string, unknown>;
    const pick = (...keys: string[]): string | undefined => {
      for (const k of keys) {
        const v = rc[k];
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
      return undefined;
    };
    recordContext = {
      trackedArtifactRef: pick('trackedArtifactRef', 'tracked_artifact_ref'),
      name: pick('name'),
      customer: pick('customer'),
      value: pick('value'),
      ageAtStep: pick('ageAtStep', 'age_at_step'),
      lastTouch: pick('lastTouch', 'last_touch'),
      owner: pick('owner'),
    };
    if (!Object.values(recordContext).some(Boolean)) {
      recordContext = undefined;
    }
  }
  return {
    ...row,
    itemId,
    postgresItemId,
    semanticItemId,
    kryspaceItemId,
    itemIdAliases,
    title: sanitizeText(typeof row.title === 'string' ? row.title : itemId) ?? itemId,
    statusPhrase:
      sanitizeText(
        (typeof row.statusPhrase === 'string' && row.statusPhrase) ||
          (typeof queueRowField(row, 'status_phrase') === 'string'
            ? (queueRowField(row, 'status_phrase') as string)
            : undefined) ||
          (typeof queueRowField(row, 'current_state') === 'string'
            ? (queueRowField(row, 'current_state') as string)
            : undefined) ||
          ''
      ) ?? '',
    domain: row.domain ?? row.filterScope,
    res,
    waypointRef,
    improvementRef,
    improvementTitle: sanitizeText(
      (typeof row.improvementTitle === 'string' && row.improvementTitle) ||
        (typeof queueRowField(row, 'improvement_title') === 'string'
          ? (queueRowField(row, 'improvement_title') as string)
          : undefined)
    ),
    assignedResourceRef,
    assignedResourceLabel,
    statusDate:
      (typeof row.statusDate === 'string' && row.statusDate) ||
      (typeof queueRowField(row, 'status_date') === 'string'
        ? (queueRowField(row, 'status_date') as string).slice(0, 10)
        : undefined),
    issueSummary: sanitizeText(
      typeof (row as ControlBridgeQueueItem).issueSummary === 'string'
        ? (row as ControlBridgeQueueItem).issueSummary
        : typeof queueRowField(row, 'issue_summary') === 'string'
          ? (queueRowField(row, 'issue_summary') as string)
          : undefined
    ),
    activityTargetState,
    activityDescription,
    coordinationNoteRef,
    blockedReason,
    deferReason,
    dependsOnRefs,
    acceptanceHints,
    workHref,
    triageRoute: triageRoute || (typeof row.triageRoute === 'string' ? row.triageRoute : undefined),
    plankDescription,
    futureCondition,
    resourceAssignment,
    assumptionText,
    estimatedDuration,
    startDate: startDate ? startDate.slice(0, 10) : undefined,
    endDate: endDate ? endDate.slice(0, 10) : undefined,
    duration,
    pickupKind,
    blockerClass: blockerClass === 'none' ? undefined : blockerClass,
    resourceAvailable,
    requiresBridgeSession,
    actorMarker,
    userResponseType,
    userResponsePrompt,
    userResponseOptions,
    userResponseTargetPropertyKeys,
    responseSchema,
    responseSchemaTargetRef,
    responseSchemaTargetState,
    responseSchemaBlockedReason,
    transitionContract,
    transitionContractStatus,
    transitionContractBlockedReason,
    recordContext,
    sequenceOrder: (() => {
      const raw =
        typeof row.sequenceOrder === 'number'
          ? row.sequenceOrder
          : queueRowField(row, 'sequence_order');
      return typeof raw === 'number' && Number.isFinite(raw) ? raw : undefined;
    })(),
  };
}

function blockedActivityCount(manifest: KryspaceRenderManifest): number {
  const attention = manifest.sections.find((s) => s.id === 'attention-queue');
  const meta = attention?.meta;
  if (meta && typeof meta.blockedActivityCount === 'number') {
    return meta.blockedActivityCount;
  }
  return 0;
}

function sectionQueue(manifest: KryspaceRenderManifest, sectionId: string): ControlBridgeQueueItem[] {
  const section = manifest.sections.find((s) => s.id === sectionId);
  const rows = (section?.queue ?? []).map(mapQueueItem);
  if (sectionId !== 'linked-workspaces') return rows;
  return rows.map((row) => {
    const statusPhrase = labelGuidancePhrase(row.statusPhrase) ?? row.statusPhrase;
    return statusPhrase === row.statusPhrase ? row : { ...row, statusPhrase };
  });
}

function healthField(manifest: KryspaceRenderManifest, key: string): string | number | boolean | null {
  const section = manifest.sections.find((s) => s.id === 'health-summary');
  const field = section?.fields?.find((f) => f.key === key);
  return field?.value ?? null;
}

export function deriveControlBridgeViewModel(manifest: KryspaceRenderManifest): ControlBridgeViewModel {
  const attention = manifest.sections.find((s) => s.id === 'attention-queue');
  const issues = manifest.sections.find((s) => s.id === 'open-issues');
  const eligibleWork = (attention?.queue ?? []).map(mapQueueItem);
  const blockedWork = sectionQueue(manifest, 'blocked-queue');
  const entropyRaw = healthField(manifest, 'overall_entropy');

  const attentionRouting =
    parseAttentionRoutingFromManifest(manifest) ?? deriveAttentionRoutingFallback({
      companyTitle: fixUtf8Mojibake(manifest.title),
      workspaceSlug: manifest.workspaceSlug,
      generatedAt: manifest.generatedAt,
      rollup: {
        bridgeSession: sanitizeRollupValue(rollupField(manifest, 'bridgeSession')),
        waypoints: rollupField(manifest, 'waypoints'),
        waypointsTotal: rollupField(manifest, 'waypointsTotal'),
        openImprovements: rollupField(manifest, 'openImprovements'),
        activeActivities: rollupField(manifest, 'activeActivities'),
        openIssues: rollupField(manifest, 'openIssues'),
      },
      recommendedFocus: eligibleWork.filter((item) => item.recommended),
      eligibleWork,
      openIssues: (issues?.queue ?? []).map(mapQueueItem),
      blockedWork,
      blockedActivityCount: blockedActivityCount(manifest),
      waypoints: sectionQueue(manifest, 'waypoint-map'),
      guidanceTimeline: sectionQueue(manifest, 'guidance-timeline'),
      linkedWorkspaces: sectionQueue(manifest, 'linked-workspaces'),
      scopeHealthQueue: sectionQueue(manifest, 'diagnose-scope-health'),
      guidanceWbsQueue: sectionQueue(manifest, 'guidance-wbs'),
      overallEntropy:
        typeof entropyRaw === 'number' && Number.isFinite(entropyRaw)
          ? entropyRaw
          : typeof entropyRaw === 'string' && entropyRaw.trim()
            ? (() => {
                const n = Number(entropyRaw);
                return Number.isFinite(n) ? n : null;
              })()
            : null,
    });

  const scopeHealthQueue = sectionQueue(manifest, 'diagnose-scope-health');
  const launchSuppressions = mcLaunchSuppressions(manifest);
  const mcDomainVgrManifest = parseMcDomainVgrFromManifest(manifest);
  let mcDomainVgr =
    mcDomainVgrManifest ??
    (launchSuppressions?.domains?.length
      ? undefined
      : scopeHealthQueue.length > 0
        ? deriveMcDomainVgrFallback(scopeHealthQueue)
        : undefined);

  const recommendedFocus = eligibleWork.filter((item) => item.recommended);
  const mcNextMoveManifest = parseMcNextMoveFromManifest(manifest);
  const mcNextMoveFallbackInput = {
    companyTitle: fixUtf8Mojibake(manifest.title),
    workspaceSlug: manifest.workspaceSlug,
    generatedAt: manifest.generatedAt,
    rollup: {
      bridgeSession: sanitizeRollupValue(rollupField(manifest, 'bridgeSession')),
      waypoints: rollupField(manifest, 'waypoints'),
      waypointsTotal: rollupField(manifest, 'waypointsTotal'),
      openImprovements: rollupField(manifest, 'openImprovements'),
      activeActivities: rollupField(manifest, 'activeActivities'),
      openIssues: rollupField(manifest, 'openIssues'),
    },
    recommendedFocus,
    eligibleWork,
    openIssues: (issues?.queue ?? []).map(mapQueueItem),
    blockedWork,
    blockedActivityCount: blockedActivityCount(manifest),
    waypoints: sectionQueue(manifest, 'waypoint-map'),
    guidanceTimeline: sectionQueue(manifest, 'guidance-timeline'),
    linkedWorkspaces: sectionQueue(manifest, 'linked-workspaces'),
    scopeHealthQueue,
    guidanceWbsQueue: sectionQueue(manifest, 'guidance-wbs'),
    overallEntropy:
      typeof entropyRaw === 'number' && Number.isFinite(entropyRaw)
        ? entropyRaw
        : typeof entropyRaw === 'string' && entropyRaw.trim()
          ? (() => {
              const n = Number(entropyRaw);
              return Number.isFinite(n) ? n : null;
            })()
          : null,
  };
  const pipelineHidden = launchSuppressions?.pipeline === 'hidden';
  let mcNextMove =
    mcNextMoveManifest ??
    (pipelineHidden
      ? undefined
      : deriveMcNextMoveFallback(
          mcNextMoveFallbackInput as ControlBridgeViewModel,
          recommendedFocus.length ? recommendedFocus : eligibleWork
        ) ?? undefined);

  const guidanceBacklogProposals = parseGuidanceBacklogFromManifest(manifest);
  const exoPanelCalendar = parseExoPanelCalendarFromManifest(manifest);
  let mcEngineeringWorkspaces = parseMcEngineeringWorkspacesFromManifest(manifest) ?? undefined;
  let bridgeSessionAssessment = parseBridgeSessionAssessmentFromManifest(manifest) ?? undefined;
  if (launchSuppressions?.domains?.length && mcDomainVgr) {
    const allowed = new Set(launchSuppressions.domains);
    const tiles = mcDomainVgr.tiles.filter((tile) => allowed.has(tile.domain));
    const mostCriticalDomain = tiles.some((tile) => tile.domain === mcDomainVgr?.mostCriticalDomain)
      ? mcDomainVgr.mostCriticalDomain
      : (tiles.find((tile) => tile.vgrValue != null)?.domain ?? null);
    mcDomainVgr = { ...mcDomainVgr, tiles, mostCriticalDomain };
  }
  if (pipelineHidden) {
    mcEngineeringWorkspaces = undefined;
    bridgeSessionAssessment = undefined;
  }
  const roleLens = parseRoleLensFromManifest(manifest);
  const flowModel = parseFlowEnvelopeFromManifest(manifest);

  return {
    companyTitle: fixUtf8Mojibake(manifest.title),
    workspaceSlug: manifest.workspaceSlug,
    generatedAt: manifest.generatedAt,
    rollup: {
      bridgeSession: sanitizeRollupValue(rollupField(manifest, 'bridgeSession')),
      waypoints: rollupField(manifest, 'waypoints'),
      waypointsTotal: rollupField(manifest, 'waypointsTotal'),
      openImprovements: rollupField(manifest, 'openImprovements'),
      activeActivities: rollupField(manifest, 'activeActivities'),
      openIssues: rollupField(manifest, 'openIssues'),
    },
    recommendedFocus,
    eligibleWork,
    openIssues: (issues?.queue ?? []).map(mapQueueItem),
    blockedWork,
    blockedActivityCount: blockedActivityCount(manifest),
    waypoints: sectionQueue(manifest, 'waypoint-map'),
    guidanceTimeline: sectionQueue(manifest, 'guidance-timeline'),
    linkedWorkspaces: sectionQueue(manifest, 'linked-workspaces'),
    scopeHealthQueue,
    guidanceWbsQueue: sectionQueue(manifest, 'guidance-wbs'),
    overallEntropy:
      typeof entropyRaw === 'number' && Number.isFinite(entropyRaw)
        ? entropyRaw
        : typeof entropyRaw === 'string' && entropyRaw.trim()
          ? (() => {
              const n = Number(entropyRaw);
              return Number.isFinite(n) ? n : null;
            })()
          : null,
    healthSource:
      typeof healthField(manifest, 'health_source') === 'string'
        ? (healthField(manifest, 'health_source') as string)
        : undefined,
    guidanceSource: sectionQueue(manifest, 'guidance-wbs').length > 0 ? 'manifest' : 'inferred',
    personalLayerSource: manifest.sections.some((s) => s.id === 'control-bridge-personal-layer')
      ? 'manifest'
      : 'proxy',
    attentionRouting,
    mcDomainVgr,
    mcDomainVgrSource: mcDomainVgrManifest ? 'manifest' : scopeHealthQueue.length > 0 ? 'fallback' : undefined,
    mostCriticalDomain: mcDomainVgr?.mostCriticalDomain ?? null,
    mcNextMove,
    mcNextMoveSource: mcNextMoveManifest ? 'manifest' : mcNextMove ? 'fallback' : undefined,
    guidanceBacklogProposals: guidanceBacklogProposals.length ? guidanceBacklogProposals : undefined,
    guidanceBacklogSource: guidanceBacklogProposals.length ? 'manifest' : 'empty',
    exoPanelCalendar: exoPanelCalendar ?? undefined,
    exoPanelCalendarSource: exoPanelCalendar ? 'manifest' : 'empty',
    mcEngineeringWorkspaces,
    bridgeSessionAssessment,
    bridgeSessionAssessmentSource: bridgeSessionAssessment ? 'manifest' : 'empty',
    roleLens: roleLens ?? null,
    roleLensSource: roleLens ? 'manifest' : 'empty',
    flowModel: flowModel ?? null,
    flowModelSource: flowModel
      ? flowModel.source === 'envelope'
        ? 'envelope'
        : 'manifest'
      : 'empty',
  };
}

export function displayValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return 'Not captured';
  }
  return String(value);
}

/** Waypoints tile: prefer "open / total" when both numeric fields exist. */
export function formatWaypointsRollup(rollup: ControlBridgeRollup): { value: string; sub?: string } {
  const open = rollup.waypoints;
  const total = rollup.waypointsTotal;
  if (typeof open === 'number' && typeof total === 'number') {
    return { value: `${open} / ${total}`, sub: 'Open waypoints / total in session' };
  }
  if (open !== null && open !== undefined && open !== '') {
    return { value: displayValue(open), sub: 'From session rollup' };
  }
  return { value: 'Not captured', sub: 'Waypoints not in manifest' };
}
